import useResizeObserver, { ResizeHandler, ResizeObserverBoxOptions, RoundingFunction } from 'use-resize-observer';
import { breakpointGutters, BreakpointSize, breakpointsWidthHelper } from '../../fundamentals/breakpoints/breakpoints';
import { RefObject, useEffect, useMemo, useState } from 'react';
import debounce from 'lodash.debounce';
import { AllowableGutter } from '../../fundamentals/spaces/space';
import { SIZES } from '../../fundamentals/sizes/sizes';

const breakpointsMapping = Object.entries(breakpointsWidthHelper).reverse();
export const getBreakpointSizeName = (width: number) => breakpointsMapping.find(([, value]: [string, number]) => value <= width)?.[0] as BreakpointSize;

export const getSizeIdentifier = (size: BreakpointSize) => ({
  isMobile: size === SIZES.X_SMALL || size === SIZES.XX_SMALL,
  isTablet: size === SIZES.SMALL || size === SIZES.MEDIUM
});

type ObservedSize = {
  width: number | undefined;
  height: number | undefined;
};

export const useResponsive = <T extends Element>(
  config?: { tolerance?: number; debounceInMs?: number },
  overrides?: {
    ref?: RefObject<T> | T | null | undefined;
    onResize?: ResizeHandler;
    box?: ResizeObserverBoxOptions;
    round?: RoundingFunction;
  }
) => {
  const { tolerance = 1, debounceInMs = 300 } = config ?? {};
  const [dimensions, setDimensions] = useState<ObservedSize>();
  const [width, setWidth] = useState<number>(1);
  const [height, setHeight] = useState<number>(1);
  const [loaded, setLoaded] = useState(false);
  const [size, setSize] = useState<BreakpointSize>(getBreakpointSizeName(1));
  const [gutter, setGutter] = useState<AllowableGutter>(breakpointGutters[getBreakpointSizeName(1)]);

  const onResize = useMemo(() => debounce(setDimensions, debounceInMs, { leading: true }), [debounceInMs]);
  const { ref } = useResizeObserver({ box: 'border-box', onResize, ...overrides });

  useEffect(() => {
    const dimensionWidth = dimensions?.width ?? tolerance;
    const dimensionHeight = dimensions?.height ?? tolerance;

    if (!tolerance || Math.abs(dimensionWidth - width) > tolerance) {
      setLoaded(!!dimensionWidth);
      setWidth(dimensionWidth);
      setSize(getBreakpointSizeName(dimensionWidth));
      setGutter(breakpointGutters[getBreakpointSizeName(dimensionWidth)]);
    }

    if (!tolerance || Math.abs(dimensionHeight - height) > tolerance) {
      setHeight(dimensionHeight);
    }
  }, [dimensions]);

  return { width, height, ref, loaded, size, gutter, ...getSizeIdentifier(size) };
};

const getWindowDimensions = () => {
  const { innerWidth: width, innerHeight: height } = globalThis.window;
  const size = getBreakpointSizeName(width);
  return {
    width,
    height,
    ...getSizeIdentifier(size)
  };
};

export const useWindowDimensions = () => {
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());
  const handleResize = () => {
    setWindowDimensions(getWindowDimensions());
  };
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowDimensions;
};
